import { paginationService } from 'core/services/paginationService';
import { filterVisibleWorkplaceUsers } from 'core/utils/filterVisibleWorkplaceUsers';

/**
 * Fetches workplace employees from /workplace_employees/users and enriches them
 * with full user data from /users. Returns an array of enriched user objects
 * sorted by lastName ascending.
 *
 * @param workplaceId - workplace identifier (string or number)
 * @returns Promise<any[]> enriched user list sorted by lastName ASC
 */
export const getEnrichedWorkplaceUsers = async (
	workplaceId: string | number
): Promise<any[]> => {
	// 1. Fetch workplace employee list (basic fields: id, firstName, lastName, email, phone, middleName)
	const wpData = await paginationService.getWithHandler(
		`workplace_employees/users?workplace=${workplaceId}`,
		undefined,
		100
	);
	const wpUsers: any[] = wpData?.member || wpData?.['hydra:member'] || [];

	if (wpUsers.length === 0) return [];

	// 2. Fetch full user data scoped to workplace when possible
	const fullData = await paginationService.getWithHandler(
		`users?workplace=${workplaceId}`,
		undefined,
		100
	);
	const fullUsers: any[] = fullData?.member || fullData?.['hydra:member'] || [];

	// 3. Build a map of full user details keyed by id
	const fullUsersMap = new Map<number, any>();
	for (const user of fullUsers) {
		const uid = typeof user.id === 'number' ? user.id : Number(user.id);
		if (!Number.isNaN(uid)) {
			fullUsersMap.set(uid, user);
		}
	}

	// 4. Merge: start with workplace employee, overlay with full user data
	const enriched = wpUsers.map((wpUser: any) => {
		const uid = typeof wpUser.id === 'number' ? wpUser.id : Number(wpUser.id);
		const full = fullUsersMap.get(uid);
		if (full) {
			return { ...full, ...wpUser, _full: full };
		}
		return wpUser;
	});

	// 5. Exclude system/hidden users and separated employees from visible lists
	const visible = filterVisibleWorkplaceUsers(enriched);

	// 6. Sort by lastName ascending
	visible.sort((a: any, b: any) =>
		(a.lastName || '').localeCompare(b.lastName || '')
	);

	return visible;
};
